import React, { useState, useMemo } from "react";
import { Form, Spinner, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import { KJUUP_COLORS } from "../helper/constants";
import '../styles/pages.css';
import "./InfoPage.css";

const TEXTS = {
  de: {
    heroTitle: "Die Kommunikationsplattform für die Baubranche",
    heroSubtitle: "KJUUP verbindet Teams, Projekte und Firmen auf einer sicheren Plattform — speziell entwickelt für Bau, Architektur und Immobilien.",
    featuresTitle: "Warum KJUUP?",
    features: [
      {
        icon: "forum",
        title: "Projektkommunikation",
        desc: "Organisieren Sie Ihre gesamte Projektkommunikation an einem Ort. Interne und externe Chats, strukturiert nach Projekten."
      },
      {
        icon: "business",
        title: "Firmenvernetzung",
        desc: "Verbinden Sie sich mit Partnern, Nachunternehmern und Auftraggebern. Ein Netzwerk, das Ihre Branche versteht."
      },
      {
        icon: "translate",
        title: "Automatische Übersetzung",
        desc: "Kommunizieren Sie in über 30 Sprachen. Nachrichten werden in Echtzeit übersetzt — perfekt für internationale Projekte."
      },
      {
        icon: "shield",
        title: "Datenschutz & Sicherheit",
        desc: "DSGVO-konform und in Europa gehostet. Ihre Daten bleiben sicher und geschützt."
      },
      {
        icon: "article",
        title: "News & Updates",
        desc: "Teilen Sie Firmennews, Projektfortschritte und Brancheninformationen direkt mit Ihrem Netzwerk."
      },
      {
        icon: "engineering",
        title: "Kapazitäten & Services",
        desc: "Finden Sie verfügbare Kapazitäten und Dienstleistungen in Ihrem Netzwerk — schnell und unkompliziert."
      }
    ],
    demoTitle: "Demo vereinbaren",
    demoSubtitle: "Erleben Sie KJUUP live. Wir zeigen Ihnen, wie unsere Plattform Ihre Projektkommunikation verbessert.",
    nameLabel: "Name *",
    namePlaceholder: "Ihr vollständiger Name",
    emailLabel: "E-Mail *",
    emailPlaceholder: "Ihre E-Mail-Adresse",
    companyLabel: "Firma",
    companyPlaceholder: "Ihre Firma (optional)",
    phoneLabel: "Telefon",
    phonePlaceholder: "Ihre Telefonnummer (optional)",
    messageLabel: "Nachricht",
    messagePlaceholder: "Was möchten Sie über KJUUP erfahren?",
    submitBtn: "Demo anfragen",
    sending: "Wird gesendet...",
    successTitle: "Vielen Dank!",
    successMsg: "Wir haben Ihre Anfrage erhalten und melden uns in Kürze bei Ihnen.",
    errorMsg: "Bitte füllen Sie alle Pflichtfelder aus.",
    backToLogin: "Zurück zum Login",
    footerText: "© 2026 KJUUP — Alle Rechte vorbehalten."
  },
  en: {
    heroTitle: "The Communication Platform for the Construction Industry",
    heroSubtitle: "KJUUP connects teams, projects, and companies on one secure platform — built specifically for construction, architecture, and real estate.",
    featuresTitle: "Why KJUUP?",
    features: [
      {
        icon: "forum",
        title: "Project Communication",
        desc: "Organize all your project communication in one place. Internal and external chats, structured by projects."
      },
      {
        icon: "business",
        title: "Company Networking",
        desc: "Connect with partners, subcontractors, and clients. A network that understands your industry."
      },
      {
        icon: "translate",
        title: "Automatic Translation",
        desc: "Communicate in over 30 languages. Messages are translated in real time — perfect for international projects."
      },
      {
        icon: "shield",
        title: "Privacy & Security",
        desc: "GDPR compliant and hosted in Europe. Your data stays safe and protected."
      },
      {
        icon: "article",
        title: "News & Updates",
        desc: "Share company news, project progress, and industry insights directly with your network."
      },
      {
        icon: "engineering",
        title: "Capacities & Services",
        desc: "Find available capacities and services within your network — quickly and easily."
      }
    ],
    demoTitle: "Schedule a Demo",
    demoSubtitle: "Experience KJUUP live. We'll show you how our platform improves your project communication.",
    nameLabel: "Name *",
    namePlaceholder: "Your full name",
    emailLabel: "Email *",
    emailPlaceholder: "Your email address",
    companyLabel: "Company",
    companyPlaceholder: "Your company (optional)",
    phoneLabel: "Phone",
    phonePlaceholder: "Your phone number (optional)",
    messageLabel: "Message",
    messagePlaceholder: "What would you like to know about KJUUP?",
    submitBtn: "Request Demo",
    sending: "Sending...",
    successTitle: "Thank you!",
    successMsg: "We've received your request and will get back to you shortly.",
    errorMsg: "Please fill in all required fields.",
    backToLogin: "Back to Login",
    footerText: "© 2026 KJUUP — All rights reserved."
  }
};

const getBrowserLang = () => {
  const lang = (navigator.language || "en").toLowerCase().split("-")[0];
  return lang === "de" ? "de" : "en";
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InfoPage = () => {
  const [lang] = useState(getBrowserLang);
  const t = useMemo(() => TEXTS[lang], [lang]);

  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = true;
    if (!form.email.trim() || !EMAIL_RE.test(form.email)) newErrors.email = true;
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    console.log("handleSubmit")
    setSending(true);
    await new Promise(r => setTimeout(r, 1200));
    setSending(false);
    setSubmitted(true);
  };

  return (
    <div className="info-page">
      {/* Header */}
      <header className="info-header">
        <div className="info-header-inner">
          <Link to="/" className="info-header-logo-link">
            <img
              src={process.env.PUBLIC_URL + "/assets/img/KJUUP-Schrift-Icon.png"}
              alt="KJUUP"
              className="info-header-logo"
            />
          </Link>
          <Link to="/" className="kj-btn kj-btn--outline kj-btn--on-surface">
            {t.backToLogin}
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="info-hero">
        <div className="info-hero-inner">
          <h1 className="info-hero-title">{t.heroTitle}</h1>
          <p className="info-hero-subtitle">{t.heroSubtitle}</p>
          <a href="#demo" className="info-hero-cta">
            {t.demoTitle}
            <span className="material-symbols-outlined info-page__cta-icon">arrow_downward</span>
          </a>
        </div>
      </section>

      {/* Features */}
      <section className="info-features">
        <h2 className="info-features-title">{t.featuresTitle}</h2>
        <div className="info-features-grid">
          {t.features.map((f, i) => (
            <div className="info-feature-card" key={i}>
              <div className="info-feature-icon">
                <span className="material-symbols-outlined">{f.icon}</span>
              </div>
              <h3 className="info-feature-card-title">{f.title}</h3>
              <p className="info-feature-card-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Demo Form */}
      <section className="info-demo" id="demo">
        <div className="info-demo-inner">
          <h2 className="info-demo-title">{t.demoTitle}</h2>
          <p className="info-demo-subtitle">{t.demoSubtitle}</p>

          {submitted ? (
            <div className="info-demo-success">
              <span className="material-symbols-outlined info-demo-success-icon">check_circle</span>
              <h3>{t.successTitle}</h3>
              <p>{t.successMsg}</p>
            </div>
          ) : (
            <Form className="info-demo-form" onSubmit={handleSubmit}>
              <div className="info-demo-form-row">
                <Form.Group className="info-demo-form-group">
                  <Form.Label>{t.nameLabel}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t.namePlaceholder}
                    value={form.name}
                    onChange={handleChange("name")}
                    className={errors.name ? "info-input-error" : ""}
                  />
                </Form.Group>
                <Form.Group className="info-demo-form-group">
                  <Form.Label>{t.emailLabel}</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder={t.emailPlaceholder}
                    value={form.email}
                    onChange={handleChange("email")}
                    className={errors.email ? "info-input-error" : ""}
                  />
                </Form.Group>
              </div>
              <div className="info-demo-form-row">
                <Form.Group className="info-demo-form-group">
                  <Form.Label>{t.companyLabel}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t.companyPlaceholder}
                    value={form.company}
                    onChange={handleChange("company")}
                  />
                </Form.Group>
                <Form.Group className="info-demo-form-group">
                  <Form.Label>{t.phoneLabel}</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder={t.phonePlaceholder}
                    value={form.phone}
                    onChange={handleChange("phone")}
                  />
                </Form.Group>
              </div>
              <Form.Group className="info-demo-form-group-full">
                <Form.Label>{t.messageLabel}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder={t.messagePlaceholder}
                  value={form.message}
                  onChange={handleChange("message")}
                />
              </Form.Group>
              {Object.keys(errors).length > 0 && (
                <Alert variant="warning" className="info-demo-error">
                  {t.errorMsg}
                </Alert>
              )}
              <button type="submit" className="kj-btn kj-btn--primary kj-btn--lg kj-btn--block" disabled={sending}>
                {sending ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t.sending}
                  </>
                ) : (
                  <>
                    {t.submitBtn}
                    <span className="material-symbols-outlined info-page__submit-icon">send</span>
                  </>
                )}
              </button>
            </Form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="info-footer">
        <p>{t.footerText}</p>
      </footer>
    </div>
  );
};

export default InfoPage;
